'use client'
import { useState } from 'react'
import { addToCart } from '@onclicks/sharedmedusa/medusaData/cart'
import { useNumberItemsCart } from '@/providers/NumberItemsCartProvider'
import { Button } from '../ui/Button'

export default function AddToCartButton({
  variantId,
  countryCode,
  inventoryQuantity,
  disabled = false,
  unavailableLabel,
  showStock = true,
}: {
  variantId: string
  countryCode: string
  inventoryQuantity: number
  disabled?: boolean
  unavailableLabel?: string
  showStock?: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [message, setMessage] = useState<{
    text: string
    type: 'success' | 'error'
  } | null>(null)
  const { addQuantity } = useNumberItemsCart()

  const handleAdd = async () => {
    setLoading(true)
    setMessage(null)
    try {
      if (quantity <= 0) {
        setMessage({ text: 'Quantité sélectionnée invalide', type: 'error' })
      } else if (quantity <= inventoryQuantity) {
        await addToCart({ variantId, quantity, countryCode })
        addQuantity(quantity)
        setMessage({ text: 'Produit ajouté au panier !', type: 'success' })
      } else {
        setMessage({
          text: `Stock insuffisant. Stock restant : ${inventoryQuantity}`,
          type: 'error',
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : ''
      const isStockError = /stock|inventory/i.test(errorMessage)

      setMessage({
        text: isStockError
          ? 'Stock insuffisant : vous avez sans doute déjà cet article en quantité maximale dans votre panier.'
          : `Erreur lors de l'ajout`,
        type: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const outOfStock = !disabled && inventoryQuantity <= 0
  const isButtonDisabled = disabled || loading || outOfStock

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(Number(e.target.value))}
          min="1"
          disabled={disabled}
          className="w-20 rounded-full border border-border py-2 text-center disabled:opacity-50"
        />
        <Button
          onClick={handleAdd}
          disabled={isButtonDisabled}
          variant="gradientOrangeToPink"
          className="flex-1 rounded-full px-6 py-3 text-base font-semibold hover:cursor-pointer disabled:cursor-default"
        >
          {loading
            ? 'Ajout...'
            : disabled
              ? (unavailableLabel ?? 'Indisponible')
              : outOfStock
                ? 'Plus de stock'
                : 'Ajouter au panier'}
        </Button>
      </div>
      {showStock && (
        <p className="mt-3 text-sm text-muted-foreground">Stock restant : {inventoryQuantity}</p>
      )}
      {message && (
        <p
          className={`mt-2 text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}
        >
          {message.text}
        </p>
      )}
    </div>
  )
}
